import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import moment from 'moment';
import { compose } from 'recompose';
import { withRouter, Link } from 'react-router';
import { Grid, Divider } from 'semantic-ui-react';
import BasicHeader from '../../components/basic-header';
import DashboardCardGroup from '../../components/dashboard-card-group';
import hasRESTClient from '../../hocs/has-rest-client';
import hasLocalStorageClient from '../../hocs/has-localstorage-client';
import CampaignsTable from '../../components/campaigns-table';
import WhatsNew from '../../components/whats-new';
import hasOrganization from '../../hocs/has-organisation';
import NewAddCampaignModal from '../../components/new-add-campaign-modal';
import ThemeSelector from '../../themes/theme-selector';
import UpgradeModal from '../../components/upgrade-modal';
import DashboardCampaignsEmptyState from '../../components/dashboard-campaigns-empty-state';
import { reportWarning } from '../../utils/error-handling-service';
import createNewCampaign from '../../utils/create-new-campaign';
import EventFeed from '../event-feed';
import OriginBasedLock from '../../components/origin-based-lock';
import OrganisationBlock from '../../components/organisation-block';

const UPGRADE_MODAL_THRESHOLD = 48;
const KASPERSKY_ORG_ID = 'ec3b6d8e-f9f0-42f4-9c09-294368cbe683';
const BDSMKTG = '63f49a4d-11bb-4942-9268-952771dedf92';

const TEAM_DYZIO_ID = 'f14d3931-0e6a-42e8-ad3c-ae2ff07e3d96';
const TEAM_TI = '0cc7aa37-af63-4f05-95ac-c42ded273c32';
const STYLIST = '8fefff6d-345b-4e74-a96f-e90f7b8651ef';
const IMPERSONATION_ORIGIN = 'https://imp.dyzio.co';

const mustBlockBasedOnOrigin = ({
  requiredOrigin,
  actualOrigin,
  organisationId,
  enforceDomainLogin,
}) => {
  const lockedOrgs = [
    TEAM_TI,
    STYLIST,
    BDSMKTG,
    // TEAM_DYZIO_ID,
  ];

  const canBeLocked = _.includes(lockedOrgs, organisationId) && _.isString(requiredOrigin);
  const mustLock = _.eq(requiredOrigin, actualOrigin) === false;
  const isImpersonationSite = actualOrigin === IMPERSONATION_ORIGIN;
  return isImpersonationSite ? false : (enforceDomainLogin || canBeLocked) && mustLock;
};

const rightTextAlign = {
  textAlign: 'right',
};

class HomePage extends Component {
  static propTypes = {
    user: PropTypes.object,
  };

  static defaultProps = {
    user: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      addCampaignModalOpen: false,
      upgradeModalOpen: false,
      isLoadingCampaigns: true,
      isLoadingTotals: true,
      campaignList: [],
    };
  }

  componentDidMount = () => {
    if (window.gtag) {
      window.gtag('event', 'conversion', { send_to: 'AW-933427904/PziqCNz3mIQBEMD1i70D' });
    }
    this.fetchCampaignList();
    this.fetchTotals();
  }

  onArchive = id => {
    const { restClient } = this.props;
    return restClient.patch(`/campaignPermission/${id}`, {
      data: {
        attributes: {
          status: 'ARCHIVED',
        },
      },
    }).then(() => this.fetchCampaignList());
  }

  onAddCampaignSubmit = values => {
    const {
      jsonAPIClient,
      userInfo,
    } = this.props;

    return createNewCampaign({
      ...values,
      jsonAPIClient,
      userInfo,
    });
  };

  fetchTotals = () => {
    const { campaignAPIClient, userInfo: { organisationId, name }, dyzioStorage } = this.props;
    this.setState({
      isLoadingTotals: true,
    }, () => {
      const requests = [
        campaignAPIClient.get(`/summary/influencers?organisationId=${organisationId}`),
        campaignAPIClient.get(`/summary/campaigns?organisationId=${organisationId}`),
        campaignAPIClient.get(`/summary/team-members?organisationId=${organisationId}`),
        campaignAPIClient.get(`/summary/tracked-assets?organisationId=${organisationId}`),
      ];

      return Promise.all(requests).then(responses => {
        const values = _.map(responses, 'data.data');
        console.log(values);
        const totals = {};
        _.forEach(values, value => _.assign(totals, value));
        const temporaryBlock = true;
        const modalShownState = dyzioStorage.get(dyzioStorage.KEYS.UPGRADE_MODAL_KEY);
        const shouldShowUpgradeModal = _.get(totals, 'totalInfluencers', 0) > UPGRADE_MODAL_THRESHOLD;
        const willShowModal = !temporaryBlock && !modalShownState.shown && shouldShowUpgradeModal;
        if (willShowModal) {
          reportWarning(`Upgrade modal displayed to ${name} from ${organisationId}`, 'Upgrade modal');
        }
        this.setState({
          totals,
          upgradeModalOpen: willShowModal,
          isLoadingTotals: false,
        });
        return {};
      });
    });
  }

  fetchCampaignList = () => {
    const { restClient, userInfo: {
      userId,
    } } = this.props;

    const queryString = encodeURIComponent(JSON.stringify({
      userId,
      search: {},
      sort: {
        field: 'campaignCreatedDate',
        order: 'DESC',
      },
      perPage: 5,
    }));

    restClient({
      method: 'get',
      url: `/campaigns/query?q=${queryString}`,
      baseURL: process.env.REACT_APP_CAMPAIGNS_GET_URL,
    }).then(response => {
      const { data: { data } } = response;
      this.setState({
        isLoadingCampaigns: false,
        campaignList: data.data,
        visibleCampaigns: data.results,
      });
      return data;
    }).catch(() => {
      this.setState({
        isLoadingCampaigns: false,
      });
    });
  }

  toggleAddCampaignModalState = () => {
    this.setState(prevState => ({
      addCampaignModalOpen: !prevState.addCampaignModalOpen,
    }));
  }

  toggleUpgradeModalState = () => {
    const { dyzioStorage } = this.props;
    dyzioStorage.set(dyzioStorage.KEYS.UPGRADE_MODAL_KEY, { shown: true });
    this.setState({
      upgradeModalOpen: false,
    });
  }

  render() {
    const { user, organisation } = this.props;
    const {
      isLoadingCampaigns,
      campaignList,
      addCampaignModalOpen,
      upgradeModalOpen,
      totals,
      isLoadingTotals,
      visibleCampaigns,
    } = this.state;
    const organisationName = user ? user.organisation.name : '';

    const isFullWhiteLabel = ThemeSelector.isFullWhiteLabel();
    const isWhiteLabel = ThemeSelector.isWhiteLabel();

    const { mixpanel } = window;

    if (mixpanel && organisationName) {
      mixpanel.identify(user.id);
      mixpanel.people.set_once({ organisation: organisationName });
    }

    const isEmptyState = !isLoadingCampaigns && _.size(campaignList) === 0;
    const influencersNumbers = _.get(totals, 'totalInfluencers', 0);

    if (!(user && organisation)) return null;
    let isFeedVisible = true;
    if (user && organisation) {
      const isKaspersky = organisation.id === KASPERSKY_ORG_ID;
      if (isKaspersky) {
        isFeedVisible = user.isUserSuperAdmin;
      }
    }

    const requiredOrigin = _.get(organisation, 'origin', null);
    const actualOrigin = _.get(window, 'origin');
    const organisationId = _.get(organisation, 'id', null);
    const enforceDomainLogin = _.get(organisation, 'enforceDomainLogin', null);
    const licenseSubscriptionType = _.get(organisation, 'licenseSubscriptionType', null);

    const isBlocked = mustBlockBasedOnOrigin({
      requiredOrigin,
      actualOrigin,
      organisationId,
      enforceDomainLogin,
    });

    const shouldRenderBlocker = organisationName && isBlocked;

    const lockProps = {
      requiredOrigin,
      actualOrigin,
      organisationName,
    };

    const blockProps = {
      organisationName,
    };

    console.log('licenseSubscriptionType', licenseSubscriptionType);

    const islapsed = _.isEqual(_.toLower(licenseSubscriptionType), 'lapsed');

    if (islapsed) {
      return <OrganisationBlock {...blockProps} />;
    }

    if (shouldRenderBlocker) {
      return <OriginBasedLock {...lockProps} />;
    }

    return (
      <div className="page">
        { !isWhiteLabel && <BasicHeader title={organisationName} /> }
        <div className="page-content">
          <Grid fluid="fluid" columns={2}>
            { !isFullWhiteLabel &&
              <Grid.Row>
                <Grid.Column width={16}>
                  <WhatsNew />
                </Grid.Column>
              </Grid.Row>
            }
            <Grid.Row>
              <Grid.Column width={isFeedVisible ? 11 : 16}>
                <DashboardCardGroup
                  organisation={organisation}
                  totals={totals}
                  visibleCampaigns={visibleCampaigns}
                  isLoadingTotals={isLoadingTotals}
                  onToggleAddCampaignModalState={this.toggleAddCampaignModalState}
                />
                <Divider hidden />
                <Divider hidden />

                <table>
                  <tr>
                    <td>
                      <h2>
                      Active Campaigns
                      </h2>
                    </td>
                    <td>
                      <p style={{ padding: '0px 20px' }}><Link to="/dashboard">View all active campaigns</Link></p>
                    </td>
                  </tr>
                </table>


                <Divider hidden />

                {
                  !isEmptyState &&
                  <div style={rightTextAlign}>
                    <CampaignsTable
                      isLoadingCampaigns={isLoadingCampaigns}
                      records={campaignList}
                      isCompact
                    />
                  </div>
                }
                {
                  isEmptyState &&
                  <DashboardCampaignsEmptyState />
                }

              </Grid.Column>

              {
                isFeedVisible &&
                <Grid.Column width={5}>
                  <div>
                    <Divider hidden />
                    <h2>
                    Organisation Activity
                    </h2>
                  </div>
                  <Divider hidden />

                  {
                    organisation &&
                    <EventFeed
                      title="Organisation Activity"
                      description="view the event history for your organisation"
                      feedType="ORGANISATION"
                      organisationId={organisation.id}
                      isCompact
                    />
                  }
                </Grid.Column>
              }

            </Grid.Row>
          </Grid>
        </div>
        {
          addCampaignModalOpen &&
          <NewAddCampaignModal
            onClose={this.toggleAddCampaignModalState}
            onAddCampaignSubmit={this.onAddCampaignSubmit}
            allowCampaignTypeChange
          />
        }
        {
          upgradeModalOpen && <UpgradeModal
            value={influencersNumbers}
            onClose={this.toggleUpgradeModalState}
          />
        }
      </div>
    );
  }
}

HomePage.propTypes = {
  restClient: PropTypes.func.isRequired,
  campaignAPIClient: PropTypes.object.isRequired,
  userInfo: PropTypes.object.isRequired,
  jsonAPIClient: PropTypes.func.isRequired,
  dyzioStorage: PropTypes.object.isRequired,
  organisation: PropTypes.object,
};

HomePage.defaultProps = {
  organisation: null,
};

const mapStateToProps = state => ({ user: state.user.user });

export default compose(
  withRouter,
  hasLocalStorageClient,
  hasRESTClient,
  hasOrganization,
  connect(mapStateToProps),
)(HomePage);



/** WEBPACK FOOTER **
 ** ./src/containers/home-page/index.js
 **/